import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { db as firestoreDb, fn, FieldValue } from 'firebase-modules';

import {
  OrganizationStatus,
  UserOnboardingStatus,
  CQAdminRole,
  AdminAuditLog,
  AdminAction,
  BugReportStatus,
  BugReportWithId,
  toDate,
  isGuestAuthUid,
} from '@creative-kit/shared';
import { matchesNormalizedSearch } from 'utils/textSearch';
import type { RootStore } from '.';
import { trackAction } from './analytics';
import { db } from './data';
import { isPartialMemberRemovalError, showMemberRemovalRetrySnackbar } from './memberRemovalRetry';

interface OrganizationData {
  id: string;
  name: string;
  status: OrganizationStatus;
  ownerEmail: string;
  ownerName: string;
  adminEmails: string[];
  adminNames: string[];
  stripeBillingLink?: string;
  memberCount: number;
  projectCount: number;
  storageGB: number;
  createdAt: Date;
  isDemo: boolean;
}

interface UserInvitation {
  id: string;
  email: string;
  name: string;
  organizationId: string;
  organizationName: string;
  status: UserOnboardingStatus;
  invitedAt: Date;
  claimedAt?: Date;
  lastActivityAt?: Date;
  setupSteps: {
    accountClaimed: boolean;
    profileCompleted: boolean;
    firstProjectCreated: boolean;
    firstMemberInvited: boolean;
  };
}

interface OrganizationUsageData {
  id: string;
  name: string;
  planName: string;
  seats: {
    used: number;
    limit: number;
  };
  storage: {
    usedGB: number;
    limitGB: number;
  };
  projects: {
    used: number;
    limit: number;
  };
}

interface CQAdminData {
  userId: string;
  email: string;
  name: string;
  role: CQAdminRole;
  addedAt: Date;
  addedBy: {
    userId: string;
    email: string | null;
  };
}

interface OrganizationMember {
  userId: string;
  email: string;
  name: string;
  role: string;
  status: string;
  joinedAt: Date;
  lastActiveAt?: Date;
}

interface MemberPermissionIssue {
  userId: string;
  email: string;
  name: string;
  organizationId: string;
  organizationName: string;
  issue: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
  suggestedFix: string;
}

interface UserData {
  uid: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  organizationId?: string;
  organizationName?: string;
  organizationRole?: string;
  isCQAdmin?: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  isActive: boolean;
  // Engagement metrics
  sessionCount?: number;
  totalSessionsCreated?: number;
  lastActivityAt?: Date;
  loginCount?: number;
}

interface SessionShareTokenData {
  id: string;
  token: string;
  sessionId: string;
  email: string;
  permission: string;
  createdBy: {
    userId: string;
    email: string | null;
  };
  createdAt: Date;
  expiresAt: Date;
  status: 'pending' | 'used' | 'expired' | 'denied';
  message?: string;
  usedAt?: Date;
  guestUserId?: string;
}

interface OrganizationSessionData {
  id: string;
  name: string;
  createdAt: Date;
  createdBy: {
    userId: string;
    email: string | null;
  };
  /** Live owner (`session.userId`); may differ from `createdBy.userId` after transfer */
  liveOwnerUserId: string;
  isArchived?: boolean;
  deletedAt?: Date;
  cardCount: number;
  moduleCount: number;
  publicAccessLevel: string;
  timeSavedInSeconds?: number;
}

export class UiCQAdminStore {
  organizations: OrganizationData[] = [];
  deletedOrganizations: OrganizationData[] = [];
  userInvitations: UserInvitation[] = [];
  organizationUsage: OrganizationUsageData[] = [];
  cqAdmins: CQAdminData[] = [];
  organizationMembers: OrganizationMember[] = [];
  permissionIssues: MemberPermissionIssue[] = [];
  bugReports: BugReportWithId[] = [];
  bugReportsLoading = false;
  bugReportsError: string | null = null;
  bugReportsTruncated = false;
  bugReportStatusFilter: BugReportStatus | '' = '';
  bugReportReporterSearch = '';
  bugReportReporterSearchDebounced = '';
  bugReportDateFrom: Date | null = null;
  bugReportDateTo: Date | null = null;
  users: UserData[] = [];
  organizationSessions: OrganizationSessionData[] = [];
  auditLogs: AdminAuditLog[] = [];
  sessionShareTokens: SessionShareTokenData[] = [];

  isLoading = false;
  isRemoving = false;
  error: string | null = null;

  constructor(private rootStore: RootStore) {
    makeObservable(this, {
      organizations: observable,
      deletedOrganizations: observable,
      userInvitations: observable,
      organizationUsage: observable,
      cqAdmins: observable,
      organizationMembers: observable,
      permissionIssues: observable,
      bugReports: observable,
      bugReportsLoading: observable,
      bugReportsError: observable,
      bugReportsTruncated: observable,
      bugReportStatusFilter: observable,
      bugReportReporterSearch: observable,
      bugReportReporterSearchDebounced: observable,
      bugReportDateFrom: observable,
      bugReportDateTo: observable,
      filteredBugReports: computed,
      users: observable,
      organizationSessions: observable,
      auditLogs: observable,
      sessionShareTokens: observable,
      isLoading: observable,
      isRemoving: observable,
      error: observable,

      loadOrganizations: action.bound,
      loadUserInvitations: action.bound,
      loadOrganizationUsage: action.bound,
      loadCQAdmins: action.bound,
      loadOrganizationMembers: action.bound,
      loadPermissionIssues: action.bound,
      loadBugReports: action.bound,
      updateBugReportStatus: action.bound,
      updateBugReportJamDevUrl: action.bound,
      updateBugReportLinearTicketId: action.bound,
      setBugReportFilters: action.bound,
      clearBugReportsError: action.bound,
      loadUsers: action.bound,
      createOrganization: action.bound,
      deleteOrganization: action.bound,
      restoreOrganization: action.bound,
      updateOrganization: action.bound,
      loadDeletedOrganizations: action.bound,
      populateDemoData: action.bound,
      resendInvitation: action.bound,
      deleteClaimTokens: action.bound,
      deleteUserInvitations: action.bound,
      addCQAdmin: action.bound,
      removeCQAdmin: action.bound,
      updateCQAdminRole: action.bound,
      inviteMember: action.bound,
      removeMember: action.bound,
      changeMemberRole: action.bound,
      transferOwnership: action.bound,
      changeUserOrganizationRole: action.bound,
      changeUserCQAdminStatus: action.bound,
      loadOrganizationSessions: action.bound,
      archiveSession: action.bound,
      deleteSession: action.bound,
      adminRestoreSession: action.bound,
      adminTransferSessionOwnership: action.bound,
      updateUser: action.bound,
      loadAuditLogs: action.bound,
      loadSessionShareTokens: action.bound,
      denySessionShareToken: action.bound,
      manuallyExpireSessionShareTokens: action.bound,
    });

    // Debounce reporter search for filtering (300ms per CLAUDE.md)
    reaction(
      () => this.bugReportReporterSearch,
      (search) => {
        runInAction(() => {
          this.bugReportReporterSearchDebounced = search;
        });
      },
      { delay: 300 },
    );
  }

  async loadOrganizations() {
    this.isLoading = true;
    this.error = null;

    try {
      // Always fetch from server so admin refresh reflects emulator/callable writes
      const serverGetOptions = { source: 'server' as const };
      // Try with orderBy first, fallback to simple get if that fails
      let snapshot;
      try {
        snapshot = await firestoreDb
          .collection('organizations')
          .orderBy('createdAt', 'desc')
          .get(serverGetOptions);
      } catch (orderByError) {
        // Fallback to simple query if orderBy fails
        snapshot = await firestoreDb.collection('organizations').get(serverGetOptions);
      }
      const orgs: OrganizationData[] = [];

      await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();

          // Skip deleted organizations (soft delete)
          if (data.deletedAt) {
            return;
          }

          // Get member count and find owner/admins
          const membersSnapshot = await firestoreDb
            .collection('organizations')
            .doc(doc.id)
            .collection('members')
            .get();

          // Find owner and admins
          let ownerEmail = '';
          let ownerName = '';
          let ownerUserId = '';
          const adminEmails: string[] = [];
          const adminNames: string[] = [];

          membersSnapshot.forEach((memberDoc) => {
            const memberData = memberDoc.data();
            if (memberData.role === 'owner') {
              ownerEmail = memberData.email || '';
              ownerName = memberData.name || '';
              ownerUserId = memberData.userId || '';
            } else if (memberData.role === 'admin') {
              adminEmails.push(memberData.email || '');
              adminNames.push(memberData.name || '');
            }
          });

          // Derive organization status from owner's canUseApp status (demo orgs stay demo)
          let orgStatus = OrganizationStatus.PENDING;
          if (data.isDemo) {
            orgStatus = OrganizationStatus.DEMO;
          } else if (ownerUserId) {
            try {
              const ownerDoc = await firestoreDb.collection('users').doc(ownerUserId).get();
              const ownerData = ownerDoc.data();
              if (ownerData?.canUseApp === true) {
                orgStatus = OrganizationStatus.ACTIVE;
              } else {
                orgStatus = OrganizationStatus.PENDING;
              }
            } catch (error) {
              // If we can't get owner status, default to PENDING
              // eslint-disable-next-line no-console
              console.warn(`Could not get owner status for org ${doc.id}:`, error);
            }
          }

          // Get project count (sessions)
          const sessionsSnapshot = await firestoreDb
            .collection('sessions')
            .where('organizationId', '==', doc.id)
            .get();

          // Calculate storage (simplified)
          let storageBytes = 0;
          sessionsSnapshot.forEach((sessionDoc) => {
            const sessionData = sessionDoc.data();
            storageBytes += JSON.stringify(sessionData).length;
          });

          const orgData = {
            id: doc.id,
            name: data.name || 'Untitled',
            status: orgStatus, // Derived from owner's canUseApp status
            ownerEmail,
            ownerName,
            adminEmails,
            adminNames,
            stripeBillingLink: data.stripeBillingLink || '',
            memberCount: membersSnapshot.size,
            projectCount: sessionsSnapshot.size,
            storageGB: storageBytes / (1024 * 1024 * 1024),
            createdAt: data.createdAt?.toDate() || new Date(),
            isDemo: data.isDemo || false,
          };

          orgs.push(orgData);
        }),
      );

      runInAction(() => {
        this.organizations = orgs;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load organizations';
        this.isLoading = false;
      });
      throw err;
    }
  }

  async loadDeletedOrganizations() {
    this.isLoading = true;
    this.error = null;

    try {
      const serverGetOptions = { source: 'server' as const };
      // Load all organizations and filter for deleted ones
      const snapshot = await firestoreDb.collection('organizations').get(serverGetOptions);
      const orgs: OrganizationData[] = [];

      await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();

          // Only include deleted organizations
          if (!data.deletedAt) {
            return;
          }

          // Get member count and find owner/admins
          const membersSnapshot = await firestoreDb
            .collection('organizations')
            .doc(doc.id)
            .collection('members')
            .get();

          // Find owner and admins
          let ownerEmail = '';
          let ownerName = '';
          let ownerUserId = '';
          const adminEmails: string[] = [];
          const adminNames: string[] = [];

          membersSnapshot.forEach((memberDoc) => {
            const memberData = memberDoc.data();
            if (memberData.role === 'owner') {
              ownerEmail = memberData.email || '';
              ownerName = memberData.name || '';
              ownerUserId = memberData.userId || '';
            } else if (memberData.role === 'admin') {
              adminEmails.push(memberData.email || '');
              adminNames.push(memberData.name || '');
            }
          });

          // Derive organization status from owner's canUseApp status (demo orgs stay demo)
          let orgStatus = OrganizationStatus.PENDING;
          if (data.isDemo) {
            orgStatus = OrganizationStatus.DEMO;
          } else if (ownerUserId) {
            try {
              const ownerDoc = await firestoreDb.collection('users').doc(ownerUserId).get();
              const ownerData = ownerDoc.data();
              if (ownerData?.canUseApp === true) {
                orgStatus = OrganizationStatus.ACTIVE;
              } else {
                orgStatus = OrganizationStatus.PENDING;
              }
            } catch (error) {
              // If we can't get owner status, default to PENDING
              // eslint-disable-next-line no-console
              console.warn(`Could not get owner status for org ${doc.id}:`, error);
            }
          }

          // Get project count (sessions)
          const sessionsSnapshot = await firestoreDb
            .collection('sessions')
            .where('organizationId', '==', doc.id)
            .get();

          // Calculate storage (simplified)
          let storageBytes = 0;
          sessionsSnapshot.forEach((sessionDoc) => {
            const sessionData = sessionDoc.data();
            storageBytes += JSON.stringify(sessionData).length;
          });

          const orgData = {
            id: doc.id,
            name: data.name || 'Untitled',
            status: orgStatus,
            ownerEmail,
            ownerName,
            adminEmails,
            adminNames,
            stripeBillingLink: data.stripeBillingLink || '',
            memberCount: membersSnapshot.size,
            projectCount: sessionsSnapshot.size,
            storageGB: storageBytes / (1024 * 1024 * 1024),
            createdAt: data.createdAt?.toDate() || new Date(),
            isDemo: data.isDemo || false,
          };

          orgs.push(orgData);
        }),
      );

      runInAction(() => {
        this.deletedOrganizations = orgs;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load deleted organizations';
        this.isLoading = false;
      });
      throw err;
    }
  }

  async loadUserInvitations() {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('userInvitations')
        .orderBy('invitedAt', 'desc')
        .get();

      const invitations: UserInvitation[] = snapshot.docs.map((doc) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = doc.data() as any;
        return {
          id: doc.id,
          email: data.email,
          name: data.name,
          organizationId: data.organizationId,
          organizationName: data.organizationName,
          status: data.status,
          invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : data.invitedAt,
          claimedAt: data.claimedAt?.toDate ? data.claimedAt.toDate() : data.claimedAt,
          lastActivityAt: data.lastActivityAt?.toDate
            ? data.lastActivityAt.toDate()
            : data.lastActivityAt,
          setupSteps: data.setupSteps,
        };
      });

      runInAction(() => {
        this.userInvitations = invitations;
        this.isLoading = false;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading user invitations:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load user invitations';
        this.isLoading = false;
      });
    }
  }

  async loadUsers() {
    this.isLoading = true;
    this.error = null;

    try {
      // Try different query approaches to find users
      let snapshot;

      // First, try a simple query without ordering
      try {
        snapshot = await firestoreDb.collection('users').get();
      } catch (simpleError) {
        // Try with orderBy as fallback
        snapshot = await firestoreDb.collection('users').orderBy('createdAt', 'desc').get();
      }

      const users: UserData[] = snapshot.docs
        .filter((doc) => !isGuestAuthUid(doc.id))
        .map((doc) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const data = doc.data() as any;

          return {
            uid: doc.id,
            email: data.email || '',
            displayName: data.name || data.displayName || '', // Use 'name' field first, fallback to 'displayName'
            avatarUrl: data.photoURL || data.avatarUrl, // Use 'photoURL' field first
            organizationId: data.organizationId,
            organizationRole: data.organizationRole,
            isCQAdmin: data.isCQAdmin || false,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            lastLoginAt: data.lastLoginAt?.toDate ? data.lastLoginAt.toDate() : undefined,
            isActive: data.canUseApp !== false, // Use 'canUseApp' field for active status
          };
        });

      // Now fetch organization and admin data for each user
      const enhancedUsers = await Promise.all(
        users.map(async (user) => {
          const {
            organizationId: userOrgId,
            organizationRole: userOrgRole,
            isCQAdmin: userIsCQAdmin,
          } = user;
          let organizationId = userOrgId;
          let organizationName = '';
          let organizationRole = userOrgRole;
          let isCQAdmin = userIsCQAdmin;

          // If user doesn't have organization data, try to find it
          if (!organizationId) {
            try {
              // Search through organizations to find this user as a member
              const orgsSnapshot = await firestoreDb.collection('organizations').get();

              // Check all organizations in parallel
              const memberChecks = orgsSnapshot.docs.map(async (orgDoc) => {
                const memberDoc = await firestoreDb
                  .collection('organizations')
                  .doc(orgDoc.id)
                  .collection('members')
                  .doc(user.uid)
                  .get();

                if (memberDoc.exists) {
                  const memberData = memberDoc.data();
                  const orgData = orgDoc.data();
                  return {
                    organizationId: orgDoc.id,
                    organizationName:
                      orgData?.name || orgData?.organizationName || 'Unknown Organization',
                    organizationRole: memberData?.role || 'member',
                  };
                }
                return null;
              });

              const results = await Promise.all(memberChecks);
              const foundOrg = results.find((result) => result !== null);

              if (foundOrg) {
                organizationId = foundOrg.organizationId;
                organizationName = foundOrg.organizationName;
                organizationRole = foundOrg.organizationRole;
              }
            } catch (error) {
              // Error finding organization - continue without organization data
            }
          } else {
            // User doc has organizationId — still resolve name + role from org/member docs
            try {
              const orgDoc = await firestoreDb
                .collection('organizations')
                .doc(organizationId)
                .get();
              if (orgDoc.exists) {
                const orgData = orgDoc.data();
                organizationName =
                  orgData?.name || orgData?.organizationName || 'Unknown Organization';
              }

              const memberDoc = await firestoreDb
                .collection('organizations')
                .doc(organizationId)
                .collection('members')
                .doc(user.uid)
                .get();
              if (memberDoc.exists) {
                organizationRole = memberDoc.data()?.role || organizationRole;
              }
            } catch (error) {
              // Error fetching organization data - continue without organization name/role
            }
          }

          // Check if user is a CQ admin
          if (!isCQAdmin) {
            try {
              const cqAdminDoc = await firestoreDb.collection('cqAdmins').doc(user.uid).get();
              isCQAdmin = cqAdminDoc.exists;
            } catch (error) {
              // Error checking admin status - continue without admin flag
            }
          }

          return {
            ...user,
            organizationId,
            organizationName,
            organizationRole,
            isCQAdmin,
          };
        }),
      );

      runInAction(() => {
        this.users = enhancedUsers;
        this.isLoading = false;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading users:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load users';
        this.isLoading = false;
      });
    }
  }

  async loadOrganizationUsage() {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb.collection('organizations').get();

      const usage: OrganizationUsageData[] = [];

      await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();

          // Get member count
          const membersSnapshot = await firestoreDb
            .collection('organizations')
            .doc(doc.id)
            .collection('members')
            .get();

          // Get project count
          const sessionsSnapshot = await firestoreDb
            .collection('sessions')
            .where('organizationId', '==', doc.id)
            .get();

          // Calculate storage
          let storageBytes = 0;
          sessionsSnapshot.forEach((sessionDoc) => {
            const sessionData = sessionDoc.data();
            storageBytes += JSON.stringify(sessionData).length;
          });

          const planLimits = data.planLimits || {
            seats: 10,
            storageGB: 5,
            projects: 50,
          };

          usage.push({
            id: doc.id,
            name: data.name || 'Untitled',
            planName: data.isDemo ? 'Demo' : 'Professional',
            seats: {
              used: membersSnapshot.size,
              limit: planLimits.seats,
            },
            storage: {
              usedGB: storageBytes / (1024 * 1024 * 1024),
              limitGB: planLimits.storageGB,
            },
            projects: {
              used: sessionsSnapshot.size,
              limit: planLimits.projects,
            },
          });
        }),
      );

      runInAction(() => {
        this.organizationUsage = usage;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load organization usage';
        this.isLoading = false;
      });
    }
  }

  async createOrganization(data: {
    name: string;
    ownerEmail: string;
    ownerName?: string;
    adminEmails: string[];
    adminNames: string[];
    stripeBillingLink?: string;
    isDemo: boolean;
  }) {
    try {
      const createOrgFn = fn.httpsCallable('createOrganization');
      const result = await createOrgFn(data);

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Organization created successfully. Claim link: ${result.data.claimLink}`,
      });

      // Reload organizations
      await this.loadOrganizations();

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create organization';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async populateDemoData(organizationId: string) {
    try {
      const populateFn = fn.httpsCallable('populateDemoData');
      const result = await populateFn({ organizationId });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message,
      });

      // Reload organizations to update counts
      await this.loadOrganizations();

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to populate demo data';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async updateOrganization(organizationId: string, name: string) {
    try {
      const trimmedName = name.trim();
      const updateOrgFn = fn.httpsCallable('updateOrganization');
      const result = await updateOrgFn({ organizationId, name: trimmedName });

      runInAction(() => {
        this.organizations = this.organizations.map((org) =>
          org.id === organizationId ? { ...org, name: trimmedName } : org,
        );
        this.deletedOrganizations = this.deletedOrganizations.map((org) =>
          org.id === organizationId ? { ...org, name: trimmedName } : org,
        );
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Organization name updated successfully',
      });

      // Reload organizations to reflect the change
      await this.loadOrganizations();

      return result.data;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update organization name';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async deleteOrganization(organizationId: string) {
    try {
      const deleteOrgFn = fn.httpsCallable('deleteOrganization');
      const result = await deleteOrgFn({ organizationId });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Organization deleted successfully',
      });

      // Reload organizations to remove deleted one from list
      await this.loadOrganizations();

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete organization';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async restoreOrganization(organizationId: string) {
    try {
      const restoreOrgFn = fn.httpsCallable('restoreOrganization');
      const result = await restoreOrgFn({ organizationId });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Organization restored successfully',
      });

      // Reload both active and deleted organizations to update lists
      await Promise.all([this.loadOrganizations(), this.loadDeletedOrganizations()]);

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore organization';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async resendInvitation(email: string, organizationId: string) {
    try {
      const resendFn = fn.httpsCallable('resendInvitation');
      const result = await resendFn({ email, organizationId });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Invitation resent to ${email}`,
      });

      return result.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend invitation';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async deleteClaimTokens(userInvitationIds: string[]) {
    try {
      const deleteFn = fn.httpsCallable('deleteClaimTokens');
      const result = await deleteFn({ userInvitationIds });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: result.data.message || 'Claim tokens deleted successfully',
      });

      // Reload user invitations to refresh the list
      await this.loadUserInvitations();

      return result.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ui-cq-admin] deleteClaimTokens error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete claim tokens';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async deleteUserInvitations(userInvitationIds: string[]) {
    try {
      const deleteFn = fn.httpsCallable('deleteUserInvitations');
      const result = await deleteFn({ userInvitationIds });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description:
          result.data.message ||
          `Successfully deleted ${result.data.deletedInvitationCount || 0} invitation(s)`,
      });

      // Reload user invitations to refresh the list
      await this.loadUserInvitations();

      return result.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ui-cq-admin] deleteUserInvitations error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user invitations';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async loadCQAdmins() {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb.collection('cqAdmins').orderBy('addedAt', 'desc').get();

      const admins: CQAdminData[] = snapshot.docs.map((doc) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = doc.data() as any;
        return {
          userId: doc.id,
          email: data.email,
          name: data.name,
          role: data.role,
          addedAt: data.addedAt?.toDate ? data.addedAt.toDate() : data.addedAt,
          addedBy: data.addedBy,
        };
      });

      runInAction(() => {
        this.cqAdmins = admins;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load CQ admins';
        this.isLoading = false;
      });
    }
  }

  async addCQAdmin(email: string, name: string, role: CQAdminRole) {
    try {
      // First, get the user by email to get their userId
      const usersSnapshot = await firestoreDb
        .collection('users')
        .where('email', '==', email)
        .limit(1)
        .get();

      if (usersSnapshot.empty) {
        throw new Error('User not found with this email address');
      }

      const userDoc = usersSnapshot.docs[0];
      const userId = userDoc.id;

      // Check if user is already a CQ admin
      const existingAdminDoc = await firestoreDb.collection('cqAdmins').doc(userId).get();
      if (existingAdminDoc.exists) {
        throw new Error('This user is already a CQ admin');
      }

      // Add to cqAdmins collection
      const currentUser = this.rootStore.authStore.user;
      await firestoreDb
        .collection('cqAdmins')
        .doc(userId)
        .set({
          email,
          name,
          role,
          addedAt: new Date(),
          addedBy: {
            userId: currentUser?.uid || '',
            email: currentUser?.email || null,
          },
        });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `CQ Admin added successfully: ${name} (${email})`,
      });

      // Reload CQ admins
      await this.loadCQAdmins();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add CQ admin';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async removeCQAdmin(userId: string) {
    try {
      const adminDoc = await firestoreDb.collection('cqAdmins').doc(userId).get();
      if (!adminDoc.exists) {
        throw new Error('CQ admin not found');
      }

      const adminData = adminDoc.data();
      await firestoreDb.collection('cqAdmins').doc(userId).delete();

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `CQ Admin removed: ${adminData?.name || adminData?.email}`,
      });

      // Reload CQ admins
      await this.loadCQAdmins();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove CQ admin';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async updateCQAdminRole(userId: string, newRole: CQAdminRole) {
    try {
      const adminDoc = await firestoreDb.collection('cqAdmins').doc(userId).get();
      if (!adminDoc.exists) {
        throw new Error('CQ admin not found');
      }

      const adminData = adminDoc.data();
      await firestoreDb.collection('cqAdmins').doc(userId).update({
        role: newRole,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `CQ Admin role updated: ${adminData?.name || adminData?.email} → ${newRole}`,
      });

      // Reload CQ admins
      await this.loadCQAdmins();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update CQ admin role';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async loadOrganizationMembers(organizationId: string) {
    this.isLoading = true;
    this.error = null;

    try {
      const membersSnapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('members')
        .get();

      const members: OrganizationMember[] = membersSnapshot.docs.map((doc) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const data = doc.data() as any;
        return {
          userId: doc.id,
          email: data.email || '',
          name: data.name || '',
          role: data.role || 'member',
          status: data.status || 'active',
          joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : data.joinedAt,
          lastActiveAt: data.lastActiveAt?.toDate ? data.lastActiveAt.toDate() : data.lastActiveAt,
        };
      });

      runInAction(() => {
        this.organizationMembers = members;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load organization members';
        this.isLoading = false;
      });
    }
  }

  async loadPermissionIssues() {
    this.isLoading = true;
    this.error = null;

    try {
      // This is a simplified implementation - in a real system, you'd analyze
      // user permissions, session access, etc. to identify issues
      const issues: MemberPermissionIssue[] = [];

      // Get all organizations and their members
      const orgsSnapshot = await firestoreDb.collection('organizations').get();

      await Promise.all(
        orgsSnapshot.docs.map(async (orgDoc) => {
          const orgData = orgDoc.data();
          const membersSnapshot = await firestoreDb
            .collection('organizations')
            .doc(orgDoc.id)
            .collection('members')
            .get();

          for (const memberDoc of membersSnapshot.docs) {
            const memberData = memberDoc.data();

            // Check for common permission issues
            if (memberData.status === 'pending') {
              issues.push({
                userId: memberDoc.id,
                email: memberData.email || '',
                name: memberData.name || '',
                organizationId: orgDoc.id,
                organizationName: orgData.name || '',
                issue: 'Pending Invitation',
                severity: 'medium',
                description: 'User has been invited but has not accepted the invitation',
                suggestedFix: 'Resend invitation email or contact user directly',
              });
            }

            if (memberData.role === 'member' && memberData.status === 'active') {
              // Check if member has been active recently
              const lastActive = memberData.lastActiveAt?.toDate();
              if (lastActive && Date.now() - lastActive.getTime() > 30 * 24 * 60 * 60 * 1000) {
                // 30 days
                issues.push({
                  userId: memberDoc.id,
                  email: memberData.email || '',
                  name: memberData.name || '',
                  organizationId: orgDoc.id,
                  organizationName: orgData.name || '',
                  issue: 'Inactive Member',
                  severity: 'low',
                  description: 'Member has not been active for over 30 days',
                  suggestedFix: 'Consider reaching out to member or removing if no longer needed',
                });
              }
            }
          }
        }),
      );

      runInAction(() => {
        this.permissionIssues = issues;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load permission issues';
        this.isLoading = false;
      });
    }
  }

  async loadBugReports() {
    this.bugReportsLoading = true;
    this.bugReportsError = null;
    try {
      const snapshot = await db.bugReports.orderBy('createdAt', 'desc').limit(51).get();
      const reports: BugReportWithId[] = [];
      snapshot.docs.slice(0, 50).forEach((doc) => {
        const data = doc.data();
        if (data) {
          reports.push({
            ...data,
            id: doc.id,
            createdAt: data.createdAt,
          } as BugReportWithId);
        }
      });
      runInAction(() => {
        this.bugReports = reports;
        this.bugReportsTruncated = snapshot.docs.length > 50;
        this.bugReportsLoading = false;
        this.bugReportsError = null;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load bug reports';
      runInAction(() => {
        this.bugReportsLoading = false;
        this.bugReportsError = message;
      });
    }
  }

  async updateBugReportStatus(bugReportId: string, status: BugReportStatus) {
    try {
      await db.bugReports.doc(bugReportId).update({ status });
      const report = this.bugReports.find((r) => r.id === bugReportId);
      if (report) {
        runInAction(() => {
          report.status = status;
        });
      }
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Bug report status updated',
      });
    } catch (err) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: err instanceof Error ? err.message : 'Failed to update status',
      });
    }
  }

  clearBugReportsError() {
    this.bugReportsError = null;
  }

  setBugReportFilters(filters: {
    status?: BugReportStatus | '';
    reporterSearch?: string;
    dateFrom?: Date | null;
    dateTo?: Date | null;
  }) {
    if (filters.status !== undefined) this.bugReportStatusFilter = filters.status;
    if (filters.reporterSearch !== undefined) this.bugReportReporterSearch = filters.reporterSearch;
    if (filters.dateFrom !== undefined) this.bugReportDateFrom = filters.dateFrom;
    if (filters.dateTo !== undefined) this.bugReportDateTo = filters.dateTo;
  }

  get filteredBugReports(): BugReportWithId[] {
    let list = [...this.bugReports];
    if (this.bugReportStatusFilter) {
      list = list.filter((r) => r.status === this.bugReportStatusFilter);
    }
    if (this.bugReportReporterSearchDebounced.trim()) {
      list = list.filter(
        (r) =>
          matchesNormalizedSearch(r.userName, this.bugReportReporterSearchDebounced) ||
          matchesNormalizedSearch(r.userEmail, this.bugReportReporterSearchDebounced),
      );
    }
    if (this.bugReportDateFrom) {
      const fromStart = this.bugReportDateFrom.getTime();
      list = list.filter((r) => toDate(r.createdAt).getTime() >= fromStart);
    }
    if (this.bugReportDateTo) {
      // Store owns end-of-day semantics for "To" filter (component passes start of day)
      const toEnd = new Date(this.bugReportDateTo);
      toEnd.setHours(23, 59, 59, 999);
      const toEndTime = toEnd.getTime();
      list = list.filter((r) => toDate(r.createdAt).getTime() <= toEndTime);
    }
    return list.sort((a, b) => {
      const daTime = toDate(a.createdAt).getTime();
      const dbTime = toDate(b.createdAt).getTime();
      return dbTime - daTime;
    });
  }

  async updateBugReportJamDevUrl(bugReportId: string, jamDevUrl: string) {
    try {
      await db.bugReports.doc(bugReportId).update({ jamDevUrl });
      const report = this.bugReports.find((r) => r.id === bugReportId);
      if (report) {
        runInAction(() => {
          report.jamDevUrl = jamDevUrl;
        });
      }
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'jam.dev link saved',
      });
    } catch (err) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: err instanceof Error ? err.message : 'Failed to save link',
      });
    }
  }

  async updateBugReportLinearTicketId(bugReportId: string, linearTicketId: string) {
    try {
      await db.bugReports.doc(bugReportId).update({ linearTicketId });
      const report = this.bugReports.find((r) => r.id === bugReportId);
      if (report) {
        runInAction(() => {
          report.linearTicketId = linearTicketId;
        });
      }
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Linear ticket saved',
      });
    } catch (err) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: err instanceof Error ? err.message : 'Failed to save Linear ticket',
      });
    }
  }

  async inviteMember(organizationId: string, email: string, role: string, name?: string) {
    try {
      const inviteMemberFn = fn.httpsCallable('inviteMember');
      const result = await inviteMemberFn({
        organizationId,
        email,
        role,
        name: name || '',
        message: 'Invited by CQ Admin',
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Invitation sent to ${email}`,
      });

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to invite member';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async removeMember(organizationId: string, userId: string) {
    if (this.isRemoving) return; // Prevent double-clicks

    runInAction(() => {
      this.isRemoving = true;
      this.error = null;
    });

    try {
      const removeMemberFn = fn.httpsCallable('removeMember');
      await removeMemberFn({
        organizationId,
        memberId: userId, // Cloud function expects memberId
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Member removed successfully',
      });

      trackAction('member_removed', 'modal');

      // Reload members
      await this.loadOrganizationMembers(organizationId);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove member';

      if (isPartialMemberRemovalError(err)) {
        showMemberRemovalRetrySnackbar(this.rootStore, errorMessage, () =>
          this.removeMember(organizationId, userId),
        );
      } else {
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: errorMessage,
        });
      }
      throw err;
    } finally {
      runInAction(() => {
        this.isRemoving = false;
      });
    }
  }

  async changeMemberRole(organizationId: string, memberId: string, newRole: string) {
    try {
      const changeMemberRoleFn = fn.httpsCallable('adminChangeMemberRole');
      await changeMemberRoleFn({
        organizationId,
        memberId,
        newRole,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: `Member role changed to ${newRole}`,
      });

      // Reload members
      await this.loadOrganizationMembers(organizationId);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change member role';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async transferOwnership(organizationId: string, newOwnerId: string) {
    try {
      const transferOwnershipFn = fn.httpsCallable('adminTransferOrganizationOwnership');
      await transferOwnershipFn({
        organizationId,
        newOwnerId,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Ownership transferred successfully',
      });

      // Reload members
      await this.loadOrganizationMembers(organizationId);

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to transfer ownership';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async changeUserOrganizationRole(userId: string, newRole: string, organizationId?: string) {
    try {
      // If no organizationId provided, find the user's current organization
      let targetOrgId = organizationId;

      if (!targetOrgId) {
        const user = this.users.find((u) => u.uid === userId);
        if (!user?.organizationId) {
          throw new Error('User is not part of any organization');
        }
        targetOrgId = user.organizationId;
      }

      // Prevent removing owner without transferring ownership
      if (newRole !== 'owner') {
        const currentUser = this.users.find((u) => u.uid === userId);
        if (currentUser?.organizationRole === 'owner') {
          throw new Error(
            'Cannot remove owner role without transferring ownership first. Use the "Transfer Ownership" option instead.',
          );
        }
      }

      // Use the existing changeMemberRole function
      await this.changeMemberRole(targetOrgId, userId, newRole);

      // Reload users to reflect changes
      await this.loadUsers();

      return { success: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to change user organization role';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async changeUserCQAdminStatus(userId: string, makeAdmin: boolean, role?: CQAdminRole) {
    try {
      if (makeAdmin) {
        // Add as CQ admin
        const user = this.users.find((u) => u.uid === userId);
        if (!user) {
          throw new Error('User not found');
        }

        // Check if already a CQ admin
        if (user.isCQAdmin) {
          throw new Error('User is already a CQ admin');
        }

        await this.addCQAdmin(user.email, user.displayName, role || CQAdminRole.SUPPORT);
      } else {
        // Remove CQ admin status
        await this.removeCQAdmin(userId);
      }

      // Reload users to reflect changes
      await this.loadUsers();

      return { success: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to change user CQ admin status';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async loadOrganizationSessions(organizationId: string) {
    this.isLoading = true;
    this.error = null;

    runInAction(() => {
      this.organizationSessions = [];
    });

    try {
      const membersSnapshot = await firestoreDb
        .collection('organizations')
        .doc(organizationId)
        .collection('members')
        .get();
      const memberIds = membersSnapshot.docs.map((d) => d.id);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessionDocsById = new Map<string, any>();

      const mergeSnapshot = (snap: { docs: Array<{ id: string }> }) => {
        snap.docs.forEach((doc) => {
          sessionDocsById.set(doc.id, doc);
        });
      };

      // Sessions explicitly linked to the org (matches org list project counts)
      let orgLinkedSnapshot;
      try {
        orgLinkedSnapshot = await firestoreDb
          .collection('sessions')
          .where('organizationId', '==', organizationId)
          .orderBy('createdAt', 'desc')
          .get();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (orderByError: any) {
        if (
          orderByError?.code === 'failed-precondition' ||
          orderByError?.message?.includes('index')
        ) {
          // eslint-disable-next-line no-console
          console.warn('Index not found for orderBy, falling back to simple query');
          orgLinkedSnapshot = await firestoreDb
            .collection('sessions')
            .where('organizationId', '==', organizationId)
            .get();
        } else {
          throw orderByError;
        }
      }
      mergeSnapshot(orgLinkedSnapshot);

      let orgSharingSnapshot;
      try {
        orgSharingSnapshot = await firestoreDb
          .collection('sessions')
          .where('sharing.organizationId', '==', organizationId)
          .where('sharing.mode', '==', 'org')
          .get();
      } catch (sharingOrgError) {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to load sessions by sharing.organizationId (composite index may be building):',
          sharingOrgError,
        );
        orgSharingSnapshot = { docs: [] as typeof orgLinkedSnapshot.docs };
      }
      mergeSnapshot(orgSharingSnapshot);

      // Member-owned sessions: client-side userId queries fail for org admins under Firestore
      // list-query rules (they can read owner sessions individually but not via `in` queries).
      // Use Admin SDK cloud function instead.
      try {
        const listMemberSessionsFn = fn.httpsCallable('listOrganizationMemberSessions');
        const result = await listMemberSessionsFn({ organizationId });
        const memberSessions = (
          result.data as {
            sessions?: Array<{
              id: string;
              name: string;
              userId: string;
              createdBy: { userId: string; email: string | null };
              createdAt: string;
              isArchived: boolean;
              deletedAt?: string;
              cardCount: number;
              moduleCount: number;
              publicAccessLevel: string;
              timeSavedInSeconds: number;
            }>;
          }
        ).sessions;

        memberSessions?.forEach((session) => {
          sessionDocsById.set(session.id, {
            id: session.id,
            data: () => ({
              name: session.name,
              userId: session.userId,
              createdBy: session.createdBy,
              createdAt: new Date(session.createdAt),
              isArchived: session.isArchived,
              deletedAt: session.deletedAt ? new Date(session.deletedAt) : undefined,
              cardIds: Array.from({ length: session.cardCount }),
              moduleIds: Array.from({ length: session.moduleCount }),
              publicAccessLevel: session.publicAccessLevel,
              timeSavedInSeconds: session.timeSavedInSeconds,
            }),
          });
        });
      } catch (memberSessionsError) {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to load member-owned sessions via cloud function, falling back to client queries:',
          memberSessionsError,
        );

        const CHUNK_SIZE = 10;
        for (let i = 0; i < memberIds.length; i += CHUNK_SIZE) {
          const chunk = memberIds.slice(i, i + CHUNK_SIZE);
          try {
            const snap = await firestoreDb
              .collection('sessions')
              .where('userId', 'in', chunk)
              .get();
            mergeSnapshot(snap);
          } catch (chunkError) {
            // eslint-disable-next-line no-console
            console.warn('Failed to load sessions for member chunk:', chunkError);
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mapDocToSession = (doc: any): OrganizationSessionData => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name || 'Untitled Session',
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt instanceof Date
            ? data.createdAt
            : new Date(),
          createdBy: data.createdBy || { userId: '', email: null },
          liveOwnerUserId: data.userId || '',
          isArchived: data.isArchived || false,
          deletedAt: data.deletedAt?.toDate
            ? data.deletedAt.toDate()
            : data.deletedAt instanceof Date
            ? data.deletedAt
            : undefined,
          cardCount: data.cardIds?.length || 0,
          moduleCount: data.moduleIds?.length || 0,
          publicAccessLevel: data.publicAccessLevel || 'PRIVATE',
          timeSavedInSeconds: data.timeSavedInSeconds || 0,
        };
      };

      const sessions: OrganizationSessionData[] = Array.from(sessionDocsById.values()).map(
        mapDocToSession,
      );

      sessions.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

      runInAction(() => {
        this.organizationSessions = sessions;
        this.isLoading = false;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading organization projects:', err);
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load organization projects';
        this.isLoading = false;
        this.organizationSessions = [];
      });
    }
  }

  async archiveSession(sessionId: string) {
    try {
      const sessionRef = firestoreDb.collection('sessions').doc(sessionId);
      await sessionRef.update({
        isArchived: true,
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Project marked as done successfully',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive project';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async deleteSession(sessionId: string) {
    try {
      const delFn = fn.httpsCallable('adminSoftDeleteSession');
      await delFn({ sessionId });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Project deleted successfully',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async adminRestoreSession(sessionId: string) {
    try {
      const restoreFn = fn.httpsCallable('adminRestoreSession');
      await restoreFn({ sessionId });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Project restored to the owner workspace.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore project';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async adminTransferSessionOwnership(sessionId: string, newOwnerId: string) {
    try {
      const transferFn = fn.httpsCallable('transferSessionOwnership');
      await transferFn({ sessionId, newOwnerId });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Ownership transferred.',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to transfer ownership';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async updateUser(
    userId: string,
    displayName?: string,
    organizationId?: string | null,
    organizationRole?: string,
    canUseApp?: boolean,
  ) {
    try {
      const updateUserFn = fn.httpsCallable('updateUser');
      const result = await updateUserFn({
        userId,
        displayName,
        organizationId: organizationId === '' ? null : organizationId,
        organizationRole: organizationRole || undefined,
        canUseApp,
      });

      const responseData = result.data as {
        memberActivationWarning?: string | null;
        message?: string;
      };

      if (responseData.memberActivationWarning) {
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: `User updated, but membership activation had an issue: ${responseData.memberActivationWarning}`,
        });
      } else {
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: responseData.message || 'User updated successfully',
        });
      }

      // Reload users to reflect changes
      await this.loadUsers();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update user';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async loadAuditLogs() {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('adminAuditLogs')
        .orderBy('performedAt', 'desc')
        .limit(1000)
        .get();

      const logs: AdminAuditLog[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          action: data.action as AdminAction,
          performedBy: {
            userId: data.performedBy?.userId || '',
            email: data.performedBy?.email || null,
            name: data.performedBy?.name || null,
          },
          performedAt: data.performedAt?.toDate() || new Date(),
          targetType: (data.targetType || 'user') as AdminAuditLog['targetType'],
          targetId: data.targetId || '',
          targetEmail: data.targetEmail || undefined,
          details: data.details || {},
          success: data.success !== false,
          errorMessage: data.errorMessage || undefined,
        };
      });

      runInAction(() => {
        this.auditLogs = logs;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load audit logs';
        this.isLoading = false;
      });
    }
  }

  async loadSessionShareTokens() {
    this.isLoading = true;
    this.error = null;

    try {
      const snapshot = await firestoreDb
        .collection('sessionShareTokens')
        .orderBy('createdAt', 'desc')
        .limit(1000)
        .get();

      const tokens: SessionShareTokenData[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          token: data.token || '',
          sessionId: data.sessionId || '',
          email: data.email || '',
          permission: data.permission || 'view',
          createdBy: {
            userId: data.createdBy?.userId || '',
            email: data.createdBy?.email || null,
          },
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate() || new Date(),
          status: data.status || 'pending',
          message: data.message || undefined,
          usedAt: data.usedAt?.toDate() || undefined,
          guestUserId: data.guestUserId || undefined,
        };
      });

      runInAction(() => {
        this.sessionShareTokens = tokens;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Failed to load session share tokens';
        this.isLoading = false;
      });
    }
  }

  async denySessionShareToken(tokenId: string) {
    try {
      const tokenRef = firestoreDb.collection('sessionShareTokens').doc(tokenId);
      const tokenDoc = await tokenRef.get();

      if (!tokenDoc.exists) {
        throw new Error('Token not found');
      }

      const tokenData = tokenDoc.data();
      if (tokenData?.status === 'denied') {
        throw new Error('Token is already denied');
      }

      if (tokenData?.status === 'used') {
        throw new Error('Cannot deny a token that has already been used');
      }

      await tokenRef.update({
        status: 'denied',
        deniedAt: FieldValue.serverTimestamp(),
      });

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Session share token denied successfully',
      });

      // Reload tokens to reflect changes
      await this.loadSessionShareTokens();

      return { success: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to deny session share token';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }

  async manuallyExpireSessionShareTokens() {
    try {
      const expireFn = fn.httpsCallable('manuallyExpireSessionShareTokens');
      const result = await expireFn({});

      const data = result.data as {
        success: boolean;
        expiredCount: number;
        errorCount: number;
        totalChecked: number;
        message: string;
      };

      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: data.message || `Successfully expired ${data.expiredCount} token(s)`,
      });

      // Reload tokens to reflect changes
      await this.loadSessionShareTokens();

      return data;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to manually expire session share tokens';
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: errorMessage,
      });
      throw err;
    }
  }
}
